/**
 * Helper utilitario para gestión dinámica de Metadatos SEO, Open Graph y Schema.org en React
 */

/**
 * Actualiza o crea una etiqueta <meta> en el <head>
 */
export function setMetaTag(nameOrProperty, key, content) {
    if (typeof document === 'undefined') return;

    let element = document.querySelector(`meta[${nameOrProperty}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(nameOrProperty, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content || '');
}

/**
 * Actualiza o crea una etiqueta <link rel="...">
 */
export function setLinkTag(rel, href) {
    if (typeof document === 'undefined') return;

    let element = document.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href || '');
}

/**
 * Inyecta datos estructurados Schema.org (JSON-LD)
 */
export function setSchemaJsonLd(schemaData) {
    if (typeof document === 'undefined' || !schemaData) return;

    const scriptId = 'json-ld-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
    }
    try {
        script.textContent = typeof schemaData === 'string' ? schemaData : JSON.stringify(schemaData);
    } catch (e) {
        console.error('Error inyectando Schema JSON-LD:', e);
    }
}

/**
 * Aplica colores de tema móvil y footer dinámicamente en el DOM y variables CSS
 */
export function applyThemeAndFooterColors(themeColor, footerColor, hoverColor) {
    if (typeof document === 'undefined') return;

    const tColor = themeColor || '#2563eb';
    const fColor = footerColor || tColor;
    const hColor = hoverColor || '#eb244b';

    // Metadatos para navegadores móviles (Chrome Android, Safari iOS, Windows Phone)
    setMetaTag('name', 'theme-color', tColor);
    setMetaTag('name', 'msapplication-navbutton-color', tColor);
    setMetaTag('name', 'apple-mobile-web-app-status-bar-style', 'black-translucent');

    // Variables CSS dinámicas aplicadas en el documento raíz
    document.documentElement.style.setProperty('--theme-color', tColor);
    document.documentElement.style.setProperty('--footer-bg', fColor);
    document.documentElement.style.setProperty('--mobile-theme-color', tColor);
    document.documentElement.style.setProperty('--nav-hover-color', hColor);
    document.documentElement.style.setProperty('--nav-hover-shadow', `${hColor}66`);

    // Persistir en localStorage para hidratación instantánea sin parpadeo
    try {
        localStorage.setItem('site_theme_color', tColor);
        localStorage.setItem('site_footer_color', fColor);
        localStorage.setItem('site_hover_color', hColor);
    } catch (e) {}
}

/**
 * Aplica configuración SEO completa al <head> del documento
 */
export function applySEO({
    title = 'Club Vende Más',
    description = 'Plataforma líder de comercio y reservas multi-tienda',
    keywords = '',
    canonical = '',
    robots = 'index, follow',
    ogTitle = '',
    ogDescription = '',
    ogImage = '',
    ogType = 'website',
    ogUrl = '',
    twitterCard = 'summary_large_image',
    twitterSite = '@clubvendemas',
    themeColor = '#2563eb',
    footerColor = '',
    hoverColor = '#eb244b',
    author = 'Club Vende Más',
    schema = null
} = {}) {
    if (typeof document === 'undefined') return;

    // 1. Title
    document.title = title;

    // 2. Metadatos estándar
    setMetaTag('name', 'description', description);
    if (keywords) setMetaTag('name', 'keywords', keywords);
    if (author) setMetaTag('name', 'author', author);
    if (robots) setMetaTag('name', 'robots', robots);
    if (canonical) setLinkTag('canonical', canonical);

    // 2.1 Color de tema móvil, Footer y sombra/hover de menú móvil
    applyThemeAndFooterColors(themeColor, footerColor || themeColor, hoverColor);

    // 3. Open Graph (Facebook, WhatsApp, LinkedIn)
    setMetaTag('property', 'og:title', ogTitle || title);
    setMetaTag('property', 'og:description', ogDescription || description);
    setMetaTag('property', 'og:type', ogType || 'website');
    setMetaTag('property', 'og:url', ogUrl || canonical || (typeof window !== 'undefined' ? window.location.href : ''));
    setMetaTag('property', 'og:site_name', 'Club Vende Más');
    if (ogImage) {
        setMetaTag('property', 'og:image', ogImage);
        setMetaTag('property', 'og:image:width', '1200');
        setMetaTag('property', 'og:image:height', '630');
    }

    // 4. Twitter / X Cards
    setMetaTag('name', 'twitter:card', twitterCard || 'summary_large_image');
    setMetaTag('name', 'twitter:title', ogTitle || title);
    setMetaTag('name', 'twitter:description', ogDescription || description);
    if (twitterSite) setMetaTag('name', 'twitter:site', twitterSite);
    if (ogImage) setMetaTag('name', 'twitter:image', ogImage);

    // 5. Schema.org JSON-LD
    if (schema) {
        setSchemaJsonLd(schema);
    }
}
